import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import md5 from 'md5';
import {
  Mail,
  Star,
  Paperclip,
  Trash2,
  X,
  ArrowLeft,
  PenSquare,
  Inbox
} from 'lucide-react';
import api from '../config/api';
import { Layout } from '../components/Layout';
import { ComposeModal } from '../components/ComposeModal';

export function WebmailPage() {
  const { mailboxId } = useParams();
  const navigate = useNavigate();

  const [folders, setFolders] = React.useState([]);
  const [messages, setMessages] = React.useState([]);
  const [selectedMessage, setSelectedMessage] = React.useState(null);
  const [currentFolder, setCurrentFolder] = React.useState('INBOX');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showCompose, setShowCompose] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState(null);

  // Poll interval (ms) - configurable via REACT_APP_EMAIL_POLL_INTERVAL
  const POLL_INTERVAL_MS = parseInt(process.env.REACT_APP_EMAIL_POLL_INTERVAL || '15000', 10);

  React.useEffect(() => {
    loadFolders();
    loadMessages(currentFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxId]);

  React.useEffect(() => {
    loadMessages(currentFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolder]);

  // Poll messages and folders periodically to show latest mail without manual refresh
  React.useEffect(() => {
    if (!mailboxId) return;
    const interval = setInterval(() => {
      loadMessages(currentFolder);
      loadFolders();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxId, currentFolder]);

  async function loadFolders() {
    try {
      const res = await api.get(`/api/webmail/${mailboxId}/folders`);
      setFolders(res.data.folders || []);
    } catch (err) {
      console.error('Failed to load folders:', err);
    }
  }

  async function loadMessages(folder = 'INBOX') {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/api/webmail/${mailboxId}/messages?folder=${folder}`);
      setMessages(res.data.messages || []);
      setLastUpdated(Date.now());
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to load messages';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMessage(messageId) {
    try {
      const res = await api.get(`/api/webmail/${mailboxId}/messages/${messageId}`);
      setSelectedMessage(res.data.message);

      // Update message as read in the list
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, is_read: true } : m
      ));
    } catch (err) {
      console.error('Failed to load message:', err);
    }
  }

  async function toggleStar(messageId, isStarred) {
    try {
      await api.patch(`/api/webmail/${mailboxId}/messages/${messageId}`, {
        is_starred: !isStarred,
      });

      // Update in list
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, is_starred: !isStarred } : m
      ));

      // Update in viewer
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, is_starred: !isStarred }));
      }
    } catch (err) {
      console.error('Failed to toggle star:', err);
    }
  }

  async function deleteMessage(messageId) {
    if (!window.confirm('Delete this message?')) return;

    try {
      await api.patch(`/api/webmail/${mailboxId}/messages/${messageId}`, {
        is_deleted: true,
      });

      // Remove from list
      setMessages(prev => prev.filter(m => m.id !== messageId));

      // Close viewer if this message was open
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (messageDate.getTime() === today.getTime() - 86400000) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }

  function extractEmail(address) {
    const match = address.match(/<(.+)>/);
    return match ? match[1] : address;
  }

  function extractName(address) {
    const match = address.match(/^(.+)\s*</);
    return match ? match[1].trim() : extractEmail(address);
  }

  function getGravatarUrl(email) {
    const cleanEmail = email.trim().toLowerCase();
    const hash = md5(cleanEmail);
    // d=mp uses a generic person icon as fallback, d=404 returns 404 if no gravatar
    return `https://www.gravatar.com/avatar/${hash}?d=identicon&s=80`;
  }

  return (
    <Layout>
      <div className="flex h-[calc(100vh-2rem)] m-4 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Sidebar - Folders */}
        <div className="w-64 bg-slate-50 border-r border-slate-200 flex flex-col">
          <div className="p-4 border-b border-slate-200">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-slate-500 hover:text-slate-700 mb-4 flex items-center gap-2 transition-colors"
            >
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
            <button
              onClick={() => setShowCompose(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl shadow-sm shadow-blue-200 transition-all flex items-center justify-center gap-2"
            >
              <PenSquare size={18} /> Compose
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {folders.map(folder => (
              <button
                key={folder.id}
                onClick={() => setCurrentFolder(folder.name)}
                className={`w-full text-left px-4 py-2.5 rounded-lg transition-colors flex justify-between items-center ${currentFolder === folder.name
                    ? 'bg-white text-blue-600 shadow-sm font-medium'
                    : 'text-slate-600 hover:bg-slate-100'
                  }`}
              >
                <span className="capitalize">{folder.name.toLowerCase()}</span>
                {folder.unread_count > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
                    {folder.unread_count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Message List */}
        <div className="w-96 border-r border-slate-200 flex flex-col bg-white">
          <div className="p-4 border-b border-slate-200 bg-white z-10 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800 capitalize">{currentFolder.toLowerCase()}</h2>
              <p className="text-xs text-slate-500 mt-1">{messages.length} messages</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => { loadMessages(currentFolder); loadFolders(); }}
                title="Refresh"
                className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1 rounded-lg border border-slate-100 bg-white"
              >
                Refresh
              </button>

              <div className="text-xs text-slate-400">
                {lastUpdated ? `Updated ${new Date(lastUpdated).toLocaleTimeString()}` : 'Not updated yet'}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="p-8 text-center text-slate-500">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading...
              </div>
            )}

            {error && (
              <div className="p-4 m-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {!loading && !error && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <Inbox size={48} className="mb-2 opacity-50" />
                <p>No messages</p>
              </div>
            )}

            {!loading && messages.map(message => (
              <div
                key={message.id}
                onClick={() => loadMessage(message.id)}
                className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${selectedMessage?.id === message.id ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'
                  } ${!message.is_read ? 'bg-slate-50' : ''}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className={`text-sm truncate pr-2 ${!message.is_read ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                    {extractName(message.from_address)}
                  </span>
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {formatDate(message.received_at)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate flex-1 ${!message.is_read ? 'font-semibold text-slate-800' : 'text-slate-500'}`}>
                    {message.subject || '(No subject)'}
                  </span>
                  {message.is_starred && (
                    <Star size={12} className="text-amber-400 fill-amber-400" />
                  )}
                  {message.has_attachments && (
                    <Paperclip size={12} className="text-slate-400" />
                  )}
                </div>

                <p className="text-xs text-slate-400 mt-1 line-clamp-1">
                  {message.body_text?.substring(0, 100) || 'No preview available'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Message Viewer */}
        <div className="flex-1 bg-white flex flex-col min-w-0">
          {!selectedMessage ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <Mail size={32} />
              </div>
              <p className="font-medium">Select a message to read</p>
            </div>
          ) : (
            <>
              {/* Message Header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 leading-tight">
                    {selectedMessage.subject || '(No subject)'}
                  </h2>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => toggleStar(selectedMessage.id, selectedMessage.is_starred)}
                      className={`p-2 rounded-lg transition-colors ${selectedMessage.is_starred
                          ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      title={selectedMessage.is_starred ? 'Unstar' : 'Star'}
                    >
                      <Star size={20} className={selectedMessage.is_starred ? 'fill-current' : ''} />
                    </button>
                    <button
                      onClick={() => deleteMessage(selectedMessage.id)}
                      className="p-2 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={20} />
                    </button>
                    <button
                      onClick={() => setSelectedMessage(null)}
                      className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors lg:hidden"
                      title="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <img
                    src={getGravatarUrl(extractEmail(selectedMessage.from_address))}
                    alt={extractName(selectedMessage.from_address)}
                    className="w-10 h-10 rounded-full shadow-sm"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg shadow-sm" style={{ display: 'none' }}>
                    {extractName(selectedMessage.from_address)[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between">
                      <span className="font-bold text-slate-900">
                        {extractName(selectedMessage.from_address)}
                      </span>
                      <span className="text-sm text-slate-500">
                        {new Date(selectedMessage.received_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 truncate">
                      &lt;{extractEmail(selectedMessage.from_address)}&gt;
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      To: {selectedMessage.to_addresses.join(', ')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Message Body */}
              <div className="flex-1 overflow-y-auto p-8 bg-white">
                {selectedMessage.body_html ? (
                  <div
                    className="prose prose-slate max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedMessage.body_html }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-slate-800 leading-relaxed">
                    {selectedMessage.body_text || '(No content)'}
                  </pre>
                )}
              </div>
            </>
          )}
        </div>

        {/* Compose Modal */}
        {showCompose && (
          <ComposeModal
            mailboxId={mailboxId}
            onClose={() => setShowCompose(false)}
            onSent={() => {
              loadMessages(currentFolder);
              loadFolders();
            }}
          />
        )}
      </div>
    </Layout>
  );
}
